import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ImportErrorsModalFooter from 'components/ImportErrorsModalFooter/ImportErrorsModalFooter';
import Separator from 'components/Separator/Separator';
import { useAppDispatch } from 'store/hooks/useApp';
import { selfDataSets } from 'store/slices/dataSets/selectors';
import { setDataSetsState } from 'store/slices/dataSets/slice';
import checkHasDuplicateError from 'utils/checkDuplicateErrors';

import i18n from '../../i18n';
import Loader from '../Loader/Loader';
import Backdrop from '../Modal/Backdrop';
import ErrorItem from './components/ErrorItem';

import type { TImportRecordsMutation } from 'components/MappingModal/interfaces/TImportRecordsMutation';
import type { TImportErrorsModalMappingModalSubmit } from 'constants/interfaces';
import type { IDataMap } from 'store/slices/dataSets/interfaces/IDataMap';
import type IProfitCenterImportRecordsRequest from 'store/slices/dataSets/interfaces/IProfitCenterImportRecordsRequest';
import type { IErrorItemData } from './interfaces/IErrorItemData';
import type IImportErrorsModal from './interfaces/IImportErrorsModal';

import styles from './scss/ImportErrorsModal.module.scss';

function ImportErrorsModal<T extends TImportRecordsMutation>({
  isImportErrorsModalVisible,
  setIsImportErrorsModalVisible,
  isLoading,
  importRecords,
  onImportError,
  disableDuplicateActions = false,
}: IImportErrorsModal<T>) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { mappingColumns } = useSelector(selfDataSets);
  const { sheetErrors } = mappingColumns || {};
  const [showButtons, setShowButtons] = useState(false);

  const errorsKeys = sheetErrors?.Main
  && Object.keys(sheetErrors.Main).length > 0 ? Object.keys(sheetErrors.Main) : [];
  const dataErrors = errorsKeys.map((key) => {
    const itemError: IErrorItemData = {
      rowNumber: key,
      items: [],
      errorMessages: sheetErrors?.Main[key] ? Object.values(sheetErrors?.Main[key]).map((error) => error) : [],
    };
    if (sheetErrors?.Main?.[key]) {
      itemError.items = Object.keys(sheetErrors?.Main[key]).map((column) => column);
    }
    return itemError;
  });
  const shouldShowDuplicateActions = disableDuplicateActions ? false : showButtons;

  useEffect(() => {
    if (isImportErrorsModalVisible) {
      document.body.style.overflow = 'hidden';
    } else if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = '';
    }
  }, [isImportErrorsModalVisible]);

  useEffect(() => {
    if (sheetErrors && !disableDuplicateActions) {
      const hasDuplicateError = checkHasDuplicateError(sheetErrors);
      setShowButtons(hasDuplicateError);
    } else {
      setShowButtons(false);
    }
  }, [disableDuplicateActions, sheetErrors]);

  const onMappingModalSubmit = (type: TImportErrorsModalMappingModalSubmit) => {
    if (disableDuplicateActions) {
      return;
    }

    if (mappingColumns) {
      const requestData: IProfitCenterImportRecordsRequest = {
        url: mappingColumns?.url || '',
        dryRun: false,
        onPkDuplicate: type,
        Main: {
          source: mappingColumns.map[0].source,
          map: mappingColumns.map[0].map as IDataMap[],
        },
      };
      setIsImportErrorsModalVisible(false);
      importRecords(requestData).unwrap().then((importRecordsResponse) => {
        dispatch(setDataSetsState({ mappingColumns: importRecordsResponse.data }));
        if (importRecordsResponse?.data?.sheetErrors) {
          dispatch(setDataSetsState({ mappingColumns: importRecordsResponse.data }));
          setIsImportErrorsModalVisible(true);
        }
      }).catch((error: unknown) => {
        if (onImportError) {
          onImportError(error);
          return;
        }

        toast.error(i18n.t('notifications.somethingWentWrong'));
      });
    }
  };

  return isImportErrorsModalVisible ? createPortal(
    <Backdrop>
      <Loader isPageLoading={isLoading} />
      <div className={styles.root}>
        <h2 className={styles.title}>{t('dataSetsManagement.importErrorsModal.title')}</h2>
        <div className={styles.content}>
          <Separator />
          <div className={styles.wrapper}>
            {dataErrors?.map((error) => (
              <ErrorItem error={error} key={window.crypto.randomUUID()} />
            ))}
          </div>
        </div>
        <ImportErrorsModalFooter
          showButtons={shouldShowDuplicateActions}
          onClose={() => { setIsImportErrorsModalVisible(false); }}
          onReplaceDuplicates={() => { onMappingModalSubmit('replace'); }}
          onSkipDuplicates={() => { onMappingModalSubmit('ignore'); }}
        />
      </div>
    </Backdrop>,
    document.body,
  ) : null;
}

export default ImportErrorsModal;
